import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { TypeaheadMatch } from 'ngx-bootstrap/typeahead';
import { Constants, TaxAuthorityStatuses } from '../../../constants.new';
import { MessageBoxService, MessageBoxButtons, MessageBoxResult } from '../../../UI-Lib/Message-Box/messagebox.service.upgrade';
import {
    AvailableTaxRateArea,
    AvailableTaxAuthority,
    ParcelTaxRateService,
    TaxAuthorityRate,
    TaxRateDetails,
    TaxAuthorityRateDetails,
    TaxSetupMinimumDTO,
    FirstEncounterSaveResult
} from './parcelTaxRateService';
import { ToastrService } from 'ngx-toastr';
import { TaxRatesService } from '../../../Assessor-Collector/Tax-Rates/tax.rates.service';
import { Subscriber } from 'rxjs';
import * as _ from 'lodash';
import { Bill } from '../../../Annual-Details/Taxes/bill.model';
import moment from 'moment-timezone';
import { HelpContentComponentConfig } from 'src/app/UI-Lib/Help-Tooltip';
import { TaxAuthorityAdjustmentHelpComponent, TaxAuthorityAdjustmentHelpComponentParams } from './TaxAuthorityAdjustmentHelp/taxAuthorityAdjustmentHelp.component';
import { AssessmentClassService } from 'src/app/Assessment-Class/assessmentClass.service';
import { AnnualDetailComponent } from 'src/app/Annual-Details/Annual-Year/annual-year.model';
import { FeatureFlagsService } from '../../../Common/FeatureFlags/feature-flags-service';

export class BillClusterTaxRateSetupModalResult {
    taxRateAreaId: number;
    taxAuthorityIds: number[];
    rowVersion: string;
}

@Component({
    templateUrl: './billClusterTaxRateSetupModal.component.html',
    standalone: false,
})
export class BillClusterTaxRateSetupModalComponent implements OnInit {
    constructor(
        private bsModalRef: BsModalRef,
        private toastr: ToastrService,
        private parcelTaxRateService: ParcelTaxRateService,
        private taxRatesService: TaxRatesService,
        private messageBoxService: MessageBoxService,
        public constants: Constants,
        private assessmentClassService: AssessmentClassService,
        private featureFlagsService: FeatureFlagsService,
    ) {}

    @ViewChild('taxRateAreaInput')
    private taxRateAreaInputRef: ElementRef;
    // loadingAll indicates the whole modal should be hidden with a loading indicator; loading just means
    // disable the controls temporarily while additional data is coming in
    loadingAll: boolean;
    loading: boolean;
    availableTaxRateAreas: AvailableTaxRateArea[];
    availableTaxAuthorities: AvailableTaxAuthority[];
    filteredAvailableTaxAuthorities: AvailableTaxAuthority[];
    model: TaxRateDetails;
    billClusterId: number;
    billClusterRowVersion: string;
    groupByCategory: boolean;
    currentTaxAuthorityGroups: {
        taxAuthorityCategoryId: number;
        categoryName: string;
        taxAuthorities: TaxAuthorityRateDetails[];
    }[];
    title: string;
    billEstimated: boolean;
    totalEstimated: boolean;
    newTaxAuthorityId: string;
    modalResultSubscriber: Subscriber<BillClusterTaxRateSetupModalResult>;
    readOnly: boolean;
    isDocumentProcessing: boolean;
    overrideSetup: BillClusterTaxRateSetupModalResult;
    processingParcelTaxSetup: TaxSetupMinimumDTO;
    processingBill: Bill;
    firstEncounterTaxAuthority: TaxAuthorityRateDetails;
    intakeAttachmentId: string;
    hasProcessingChanges: boolean;
    calcProjected: boolean;
    annualDetailComponents: AnnualDetailComponent[];
    // I'd like to use Angular forms handling for a dirty flag, but it seems this works much better
    // for reactive forms than it does for template-driven forms. For now, just use a flag.
    isDirty: boolean;
    assessmentComponents: Weissman.Model.Assessments.AssessmentComponent[];
    exemptionAssessmentComponents: Weissman.Model.Assessments.AssessmentComponent[];
    firstEncounterSavedCallback: (result: FirstEncounterSaveResult) => Promise<void>;
    // The assessment revision whose taxable-value mappings are shown (bound to the Revision dropdown).
    selectedAnnualAssessmentId: number;

    get isManualTaxableValueMode(): boolean {
        return (
            !!this.featureFlagsService.featureFlags?.enableTaxableValueOverride &&
            !!this.model?.isManualTaxableValueMode
        );
    }

    // Mappings are stored per assessment revision; the dropdown only applies when there is more than one revision to
    // choose from. Document processing and overridden setups have no persisted revision data to show.
    get showRevisionSelect(): boolean {
        return (
            this.isManualTaxableValueMode &&
            !this.isDocumentProcessing &&
            !this.overrideSetup &&
            this.model.revisions?.length > 1
        );
    }

    // Only the newest revision's mappings drive the bills, so any other revision is edited in a mappings-only mode:
    // the rest of the dialog is read-only and saving does not recalculate the bills.
    get isEarlierRevisionSelected(): boolean {
        if (!this.showRevisionSelect) {
            return false;
        }

        const newestRevision = this.model.revisions.reduce((newest, revision) =>
            revision.revisionNum > newest.revisionNum ? revision : newest,
        );

        return this.model.selectedAnnualAssessmentId !== newestRevision.annualAssessmentId;
    }

    // A revision without taxable-value rows leaves the taxable value selectors with nothing but "-- Unmapped --".
    get hasNoTaxableValuesToMap(): boolean {
        return this.isManualTaxableValueMode && !this.model?.availableTaxableValues?.length;
    }

    async ngOnInit() {
        this.loadingAll = true;
        this.newTaxAuthorityId = '';
        this.firstEncounterTaxAuthority = null;
        this.groupByCategory = this.taxRatesService.getIsGroupByEnabled();

        try {
            // The model is usually determined from the bill cluster, however document processing allows for an
            // overrideSetup object. If such an object is present, get the model based on the tax rate area, tax
            // authorities, or from the parcel collector as appropriate.
            let modelPromise: Promise<TaxRateDetails>;
            if (!this.overrideSetup) {
                // Another wrinkle, it's possible to change the parcel setup on document processing before
                // opening this modal. In that case, the API will have the actual saved values, and we might
                // need the intermediate values (saved in processingParcelTaxSetup). getTaxDetailsForParent
                // automatically adjusts for this, but in this case use special handling to re-fetch the values
                // if this is synched to the parcel.
                modelPromise = new Promise(async (res) => {
                    let details = await this.getCurrentTaxDetails();
                    if (this.processingParcelTaxSetup && details.usingParentRates) {
                        details = await this.getTaxDetailsForParent();
                    }
                    res(details);
                });
            } else if (this.overrideSetup.taxRateAreaId) {
                modelPromise = this.getTaxDetailsByRateArea(this.overrideSetup.taxRateAreaId);
            } else if (this.overrideSetup.taxAuthorityIds && this.overrideSetup.taxAuthorityIds.length > 0) {
                modelPromise = this.getTaxDetailsByAuthorities(this.overrideSetup.taxAuthorityIds);
            } else {
                modelPromise = this.getTaxDetailsForParent();
            }

            [this.availableTaxAuthorities, this.availableTaxRateAreas, this.model, this.assessmentComponents] =
                await Promise.all([
                    this.parcelTaxRateService.getAvailableTaxAuthoritiesForBillCluster(this.billClusterId),
                    this.parcelTaxRateService.getAvailableTaxRateAreasForBillCluster(this.billClusterId),
                    modelPromise,
                    this.assessmentClassService.getAssessmentComponents(),
                ]);

            this.onModelChange(true);
            this.isDirty = false;
        } finally {
            this.loadingAll = false;
            this.exemptionAssessmentComponents = this.assessmentComponents.filter(
                (ac) => ac.assessmentComponentTypeID === 2,
            );
        }
    }

    onTaxRateAreaTextChange() {
        if (this.taxRateAreaInputRef.nativeElement.value == '') {
            this.clearSelectedTaxRateAreaId();
        } else {
            this.taxRateAreaInputRef.nativeElement.value = this.model.name;
        }
    }

    async onTaxRateAreaIdChange(event: TypeaheadMatch) {
        try {
            this.loadingAll = true;
            this.model = await this.getTaxDetailsByRateArea(event.item.taxRateAreaId);
            this.onModelChange();
        } finally {
            this.loadingAll = false;
        }
    }

    clearSelectedTaxRateAreaId() {
        Object.assign(this.model, { taxRateAreaId: null, name: null, code: null });
        this.isDirty = true;
    }

    async addNewTaxAuthority(newAvailableTaxAuthority: AvailableTaxAuthority) {
        this.loading = true;
        try {
            const newTaxAuthority = await this.getTaxAuthority(newAvailableTaxAuthority.taxAuthorityId);
            if (newTaxAuthority) {
                this.model.taxAuthorities.push(newTaxAuthority);
                await this.refetchTaxDetailsForAuthorities();
                this.onModelChange();
            }
        } finally {
            this.loading = false;
        }
    }

    async removeTaxAuthority(taxAuthority: TaxAuthorityRate) {
        // Disable the controls while the model is being re-fetched, so that nothing is edited on the model that is
        // about to be replaced.
        this.loading = true;
        try {
            this.model.taxAuthorities = this.model.taxAuthorities.filter(
                (a) => a.taxAuthorityId !== taxAuthority.taxAuthorityId,
            );
            await this.refetchTaxDetailsForAuthorities();
            this.onModelChange();
        } finally {
            this.loading = false;
        }
    }

    async refetchTaxDetailsForAuthorities() {
        // Re-fetch the Tax Details for all Tax Authorities to get totals re-computed. The server only knows the saved
        // taxable value mappings, so carry over the selections that have not been saved yet.
        const unsavedSelections = this.getTaxableValueSelections();
        const loadedAnnualAssessmentId = this.model.selectedAnnualAssessmentId;
        const taxAuthorityIds = this.model.taxAuthorities.map((a) => a.taxAuthorityId);
        this.model = await this.getTaxDetailsByAuthorities(taxAuthorityIds);

        if (!this.isManualTaxableValueMode) {
            return;
        }

        // The server always returns the current revision. When a newer revision was created since the dialog was
        // loaded, the earlier selections refer to taxable value rows of the older revision and cannot be carried over.
        if (loadedAnnualAssessmentId && this.model.selectedAnnualAssessmentId !== loadedAnnualAssessmentId) {
            this.toastr.warning(
                'A newer revision of the assessment was loaded. The selections shown are those of the latest revision.',
            );

            return;
        }

        this.applyTaxableValueSelections(unsavedSelections);
    }

    async refreshFromModel() {
        if (this.model.usingParentRates) {
            this.model = await this.getTaxDetailsForParent();
        } else if (this.model.taxRateAreaId) {
            this.model = await this.getTaxDetailsByRateArea(this.model.taxRateAreaId);
        } else {
            this.model = await this.getTaxDetailsByAuthorities(
                this.model.taxAuthorities ? this.model.taxAuthorities.map((a) => a.taxAuthorityId) : [],
            );
        }
        this.onModelChange(true);
    }

    onModelChange(skipDirtySet?: boolean) {
        if (!skipDirtySet) {
            this.isDirty = true;
        }
        this.selectedAnnualAssessmentId = this.model.selectedAnnualAssessmentId;
        this.filteredAvailableTaxAuthorities = _.sortBy(
            this.availableTaxAuthorities.filter(
                (avail) => !this.model.taxAuthorities.some((a) => avail.taxAuthorityId === a.taxAuthorityId),
            ),
            [
                function (ta) {
                    return ta.name.toLowerCase();
                },
            ],
        );
        this.totalEstimated = this.model.taxAuthorities.some(
            (ta) => ta.taxAuthorityStatusId !== TaxAuthorityStatuses.Actual,
        );
        if (this.groupByCategory) {
            const distinctCategoryIds = [...new Set(this.model.taxAuthorities.map((ta) => ta.taxAuthorityCategoryId))];
            this.currentTaxAuthorityGroups = _.chain(distinctCategoryIds)
                .map((c) => {
                    return {
                        taxAuthorityCategoryId: c,
                        categoryName: c ? this.constants.TaxAuthorityCategories[c].displayName : null,
                        taxAuthorities: this.model.taxAuthorities
                            .filter((ta) => ta.taxAuthorityCategoryId == c)
                            .sort((x, y) => x.name.localeCompare(y.name)),
                    };
                })
                .sortBy('categoryName')
                .value();
        } else {
            this.currentTaxAuthorityGroups = [
                {
                    taxAuthorityCategoryId: 0,
                    categoryName: null,
                    taxAuthorities: this.model.taxAuthorities.sort((x, y) => x.name.localeCompare(y.name)),
                },
            ];
        }
    }

    onGroupByCategoryChanged($event: boolean) {
        this.groupByCategory = $event;
        this.taxRatesService.setIsGroupByEnabled($event);
        this.onModelChange(true);
    }

    async onRevisionChange(annualAssessmentId: number): Promise<void> {
        const previousAnnualAssessmentId = this.model.selectedAnnualAssessmentId;

        if (annualAssessmentId === previousAnnualAssessmentId) {
            return;
        }

        if (this.isDirty) {
            const confirmResult = await this.messageBoxService.open({
                message: 'You have unsaved changes; are you sure you wish to switch revisions?',
                buttons: MessageBoxButtons.OKCancel,
            });

            if (confirmResult !== MessageBoxResult.OK) {
                // The dropdown is bound two-way, so put the previous revision back to restore what it displays.
                this.selectedAnnualAssessmentId = previousAnnualAssessmentId;

                return;
            }
        }

        try {
            this.loading = true;
            this.model = await this.parcelTaxRateService.getTaxRateDetails(
                this.billClusterId,
                this.processingBill,
                annualAssessmentId,
            );
            this.onModelChange(true);
            this.isDirty = false;
        } catch (error) {
            // Keep showing the revision that is still loaded.
            this.selectedAnnualAssessmentId = previousAnnualAssessmentId;

            throw error;
        } finally {
            this.loading = false;
        }
    }

    async onUsingParentRatesChanged($event: boolean) {
        let confirmed = !$event ? true : false;
        if ($event) {
            confirmed = await this.confirmSwitchToParentRates();
        }
        if (confirmed) {
            this.model.usingParentRates = $event;
            if ($event) {
                try {
                    this.loadingAll = true;
                    this.model = await this.getTaxDetailsForParent();
                } finally {
                    this.loadingAll = false;
                }
            } else {
                // When the Bill Cluster has no local setup, getCurrentTaxDetails falls back to
                // the parent defaults and returns usingParentRates=true. Keep the displayed
                // parent values as the local draft so the unchecked state can be saved.
                this.model.usingParentRates = false;
            }
        } else {
            this.model.usingParentRates = false;
        }
        this.onModelChange();
    }

    async confirmSwitchToParentRates(): Promise<boolean> {
        const result = await this.messageBoxService.open({
            message: "Are you sure you want to discard this configuration and Sync to parcel's Collector defaults?",
            buttons: MessageBoxButtons.OKCancel,
        });

        return result === MessageBoxResult.OK;
    }

    async closeModal() {
        let shouldClose = true;

        if (this.isDirty) {
            const confirmResult = await this.messageBoxService.open({
                message: 'You have unsaved changes; are you sure you wish to leave?',
                buttons: MessageBoxButtons.OKCancel,
            });

            shouldClose = confirmResult === MessageBoxResult.OK;
        }

        if (shouldClose) {
            this.bsModalRef.hide();
        }
    }

    async save() {
        if (!this.isDirty) {
            this.bsModalRef.hide();
            return;
        }

        if (!(await this.confirmUnmappedTaxAuthorities())) {
            // Cancel discards the unsaved changes and closes the dialog, without the unsaved-changes prompt.
            this.bsModalRef.hide();
            return;
        }

        this.loadingAll = true;
        try {
            const modalResult = await this.getModalResult();
            this.modalResultSubscriber.next(modalResult);
            this.modalResultSubscriber.complete();
            this.bsModalRef.hide();
            if (!this.isDocumentProcessing) {
                this.toastr.success(
                    this.isEarlierRevisionSelected
                        ? 'Taxable value mappings of the selected revision saved'
                        : 'Bill Cluster Tax Rate Setup Saved',
                );
            }
        } catch (err) {
            this.loadingAll = false;
            throw err;
        }
    }

    // In Manual Taxable Value mode a Tax Authority with no taxable value selected contributes $0 tax, so the user
    // must explicitly acknowledge that before saving. Not applicable to document processing, which defers the save.
    private async confirmUnmappedTaxAuthorities(): Promise<boolean> {
        if (!this.isManualTaxableValueMode || this.isDocumentProcessing) {
            return true;
        }

        const unmappedNames = this.model.taxAuthorities
            .filter((ta) => !ta.annualAssessmentTaxableValueId)
            .map((ta) => ta.name)
            .sort((x, y) => x.localeCompare(y));

        if (unmappedNames.length === 0) {
            return true;
        }

        const consequence = this.isEarlierRevisionSelected
            ? 'no taxable value selected for this revision and will use $0 in Appeal Savings'
            : 'no taxable value selected and will contribute $0 tax';
        const result = await this.messageBoxService.open({
            message: `${unmappedNames.length === 1 ? 'This Tax Authority has' : 'These Tax Authorities have'} ${consequence}: ${unmappedNames.join(', ')}. Do you want to continue?`,
            buttons: MessageBoxButtons.OKCancel,
        });

        return result === MessageBoxResult.OK;
    }

    // Manual Taxable Value mode: applies the selected Annual Assessment Taxable Value row to the
    // Tax Authority line locally so the dialog's Annual Tax Amount reflects the change immediately;
    // the authoritative recalculation happens server-side once the dialog is saved.
    onTaxableValueChange(taxAuthority: TaxAuthorityRateDetails, annualAssessmentTaxableValueId: number) {
        if (!this.isManualTaxableValueMode) {
            return;
        }

        taxAuthority.annualAssessmentTaxableValueId = annualAssessmentTaxableValueId ?? null;
        const selected = this.model.availableTaxableValues?.find(
            (o) => o.annualAssessmentTaxableValueId === taxAuthority.annualAssessmentTaxableValueId,
        );
        const assessedValue = selected ? selected.assessedValue : 0;
        taxAuthority.assessedValue = assessedValue;
        taxAuthority.assessedValueAdjustment = 0;
        taxAuthority.marketValue = selected ? selected.marketValue : 0;
        taxAuthority.marketValueAdjustment = 0;
        taxAuthority.taxableMarketValue = taxAuthority.marketValue;
        taxAuthority.taxableAssessedValue = assessedValue;
        taxAuthority.annualTaxAmount = this.roundCurrency(assessedValue * taxAuthority.rate);
        this.model.totalTaxAmount = this.roundCurrency(
            this.model.taxAuthorities.reduce((total, authority) => total + (authority.annualTaxAmount || 0), 0),
        );
        this.model.calculatedTaxAmount = this.roundCurrency(
            this.model.totalTaxAmount + (this.model.directAssessmentTaxAmount || 0),
        );
        this.model.taxAmountVariance = this.roundCurrency(
            (this.model.totalBillTaxAmount || 0) - this.model.calculatedTaxAmount,
        );
        this.onModelChange();
    }

    onNewTaxAuthorityChange(newTaxAuthorityId: string) {
        this.newTaxAuthorityId = '';
        if (newTaxAuthorityId) {
            this.addNewTaxAuthority(this.availableTaxAuthorities.find((ta) => ta.taxAuthorityId == +newTaxAuthorityId));
        }
    }

    async getTaxAuthority(taxAuthorityId: number) {
        return (await this.getTaxDetailsByAuthorities([taxAuthorityId])).taxAuthorities[0];
    }

    async getCurrentTaxDetails() {
        return await this.parcelTaxRateService.getTaxRateDetails(this.billClusterId, this.processingBill);
    }

    async getTaxDetailsByAuthorities(taxAuthorityIds: number[]) {
        return await this.parcelTaxRateService.searchTaxRateDetails(
            this.billClusterId,
            null,
            taxAuthorityIds,
            this.processingBill,
        );
    }

    async getTaxDetailsByRateArea(taxRateAreaId: number) {
        return await this.parcelTaxRateService.searchTaxRateDetails(
            this.billClusterId,
            taxRateAreaId,
            null,
            this.processingBill,
        );
    }

    async getTaxDetailsForParent() {
        let result: TaxRateDetails;

        if (!this.processingParcelTaxSetup) {
            result = await this.parcelTaxRateService.getCollectorTaxRateDetails(
                this.billClusterId,
                this.processingBill,
            );
        } else {
            if (this.processingParcelTaxSetup.taxRateAreaId) {
                result = await this.getTaxDetailsByRateArea(this.processingParcelTaxSetup.taxRateAreaId);
            } else if (this.processingParcelTaxSetup.taxAuthorities && this.processingParcelTaxSetup.taxAuthorities) {
                result = await this.getTaxDetailsByAuthorities(
                    this.processingParcelTaxSetup.taxAuthorities.map((ta) => ta.taxAuthorityId),
                );
            } else {
                result = await this.getTaxDetailsByAuthorities([]);
            }
            result.usingParentRates = true;
        }

        return result;
    }

    showFirstEncounter(taxAuthority: TaxAuthorityRateDetails) {
        return (
            this.isDocumentProcessing &&
            taxAuthority &&
            taxAuthority.taxAuthorityStatusId === TaxAuthorityStatuses.Estimated
        );
    }

    startFirstEncounter(taxAuthority: TaxAuthorityRateDetails) {
        if (this.isDocumentProcessing) {
            this.firstEncounterTaxAuthority = taxAuthority;
        }
    }

    async endFirstEncounter(reload: boolean, result?: FirstEncounterSaveResult) {
        this.firstEncounterTaxAuthority = null;
        if (reload) {
            this.loading = true;
            try {
                result.updateVersion = this.hasProcessingChanges && !this.calcProjected;
                if (this.isDirty) {
                    result.inProcessTaxSetupChanges = await this.getModalResult();
                }
                await this.firstEncounterSavedCallback(result);
                if (!result.updateVersion) {
                    this.bsModalRef.hide();
                } else {
                    await this.refreshFromModel();
                }
            } finally {
                this.loading = false;
            }
        }
    }

    rateClass(taxAuthority: TaxAuthorityRateDetails) {
        switch (taxAuthority.taxAuthorityStatusId) {
            case TaxAuthorityStatuses.Estimated:
                return 'estimated';
            case TaxAuthorityStatuses.Pending:
                return 'pending';
            default:
                return '';
        }
    }

    taTooltip(taxAuthority: TaxAuthorityRateDetails) {
        let result: string = null;
        if (taxAuthority.taxAuthorityStatusId === TaxAuthorityStatuses.Pending) {
            if (taxAuthority.qcRequestTimeUtc) {
                result = `QC requested at ${moment(taxAuthority.qcRequestTimeUtc).tz('America/Chicago').format('M/D/Y h:mm a')}`;
                if (taxAuthority.qcRequestUserFullName) {
                    result += ` by ${taxAuthority.qcRequestUserFullName}`;
                }
            }
        }

        return result;
    }

    taxableAssessmentTypeName(taxableAssessmentTypeId: number) {
        if (!taxableAssessmentTypeId) {
            return null;
        }

        const taxableAssessmentType = this.constants.TaxableAssessmentTypesForTrueRate[taxableAssessmentTypeId];

        if (!taxableAssessmentType) {
            return null;
        }
        return taxableAssessmentType.displayName;
    }

    taxableValueLabel(annualAssessmentTaxableValueId: number): string {
        if (!annualAssessmentTaxableValueId) {
            return '-- Unmapped --';
        }
        const option = this.model.availableTaxableValues?.find(
            (o) => o.annualAssessmentTaxableValueId === annualAssessmentTaxableValueId,
        );
        return option ? option.label : '-- Unmapped --';
    }

    taxAuthorityCategoryName(taxAuthorityCategoryId: number) {
        if (!taxAuthorityCategoryId) {
            return null;
        }
        const taxAuthorityCategory = this.constants.TaxAuthorityCategories[taxAuthorityCategoryId];
        if (!taxAuthorityCategory) {
            return null;
        }
        return taxAuthorityCategory.displayName;
    }

    private async getModalResult(): Promise<BillClusterTaxRateSetupModalResult> {
        let taxRateAreaId: number = null;
        let taxAuthorityIds: number[] = [];
        // If we're using parent rates, clear out all existing entries. Otherwise, prefer the
        // taxRateAreaId if it exists, falling back to the model's tax authorities array otherwise.
        if (!this.model.usingParentRates) {
            if (this.model.taxRateAreaId) {
                taxRateAreaId = this.model.taxRateAreaId;
            } else {
                taxAuthorityIds = this.model.taxAuthorities.map((ta) => ta.taxAuthorityId);
            }
        }

        // Only relevant when the Annual Year is in Manual Taxable Value mode; each Tax Authority's
        // selected taxable-value row (or null when left unmapped) is submitted alongside the save.
        const taxAuthorityTaxableValueMappings: Core.TaxAuthorityTaxableValueMappingRequest[] = this.isManualTaxableValueMode
            ? this.model.taxAuthorities.map((ta) => ({
                  taxAuthorityId: ta.taxAuthorityId,
                  annualAssessmentTaxableValueId: ta.annualAssessmentTaxableValueId ?? null,
              }))
            : [];

        let modalResult = {
            rowVersion: <string>null,
            taxRateAreaId,
            taxAuthorityIds,
        };

        if (!this.isDocumentProcessing) {
            const isMappingsOnly = this.isEarlierRevisionSelected;
            const saveResult = await this.parcelTaxRateService.saveTaxRateDetails(
                this.billClusterId,
                this.billClusterRowVersion,
                taxRateAreaId,
                taxAuthorityIds,
                taxAuthorityTaxableValueMappings,
                this.model.usingParentRates,
                this.model.selectedAnnualAssessmentId ?? null,
                isMappingsOnly,
                this.model.mappingsVersion ?? null,
            );

            // Re-read the saved details so the next display uses the server-calculated tax amount,
            // including the payment recalculation performed by the save operation. A mappings-only save does not
            // recalculate the bill, and the re-read would show the latest revision instead of the saved one.
            if (!isMappingsOnly) {
                await this.refreshFromModel();
            }
            modalResult = {
                rowVersion: saveResult.billClusterRowVersion,
                taxRateAreaId,
                taxAuthorityIds,
            };
        }

        return modalResult;
    }

    getAdjustmentContentComponent(
        taxAuthority: TaxAuthorityRateDetails,
    ): HelpContentComponentConfig<TaxAuthorityAdjustmentHelpComponent, TaxAuthorityAdjustmentHelpComponentParams> {
        let assessmentComponentsInForecast = this.assessmentComponents.filter((ac) =>
            this.annualDetailComponents.some(
                (adc) => ac.componentName === adc.componentName && ac.sequence === adc.sequence,
            ),
        );

        return {
            component: TaxAuthorityAdjustmentHelpComponent,
            componentParams: {
                taxAuthority: taxAuthority,
                assessmentComponents: assessmentComponentsInForecast,
                allowTotalAVOnlyText: false,
            },
        };
    }

    showToolTip(taxAuthority: TaxAuthorityRateDetails): boolean {
        //if exemption type is all or none, then there will not be any components on the tax authority's certain exemptions list
        //must have at least two exemption components on the assessment to show the icon
        return (
            this.exemptionAssessmentComponents.length > 1 &&
            this.exemptionAssessmentComponents.length >
                taxAuthority.certainExemptionsTaxAuthorityAssessmentComponentIds.length &&
            taxAuthority.certainExemptionsTaxAuthorityAssessmentComponentIds.length > 0 &&
            taxAuthority.assessedValueAdjustment > 0
        );
    }

    private getTaxableValueSelections(): Map<number, number | null> {
        return new Map(
            this.model.taxAuthorities.map((ta): [number, number | null] => [
                ta.taxAuthorityId,
                ta.annualAssessmentTaxableValueId ?? null,
            ]),
        );
    }

    // Re-applies earlier selections to the Tax Authorities of a freshly fetched model. A selection that already matches
    // the fetched value (which includes a newly added Tax Authority, whose own fetch is its baseline) is left as the
    // server returned it, and so is a selection whose taxable value is no longer offered.
    private applyTaxableValueSelections(selections: Map<number, number | null>): void {
        // onTaxableValueChange re-sorts the Tax Authorities in place, so iterate over a copy.
        for (const taxAuthority of [...this.model.taxAuthorities]) {
            // Defensive: the fetch is made for the same Tax Authorities the selections were taken from.
            if (!selections.has(taxAuthority.taxAuthorityId)) {
                continue;
            }

            const selectedId = selections.get(taxAuthority.taxAuthorityId);
            const isStillAvailable =
                selectedId === null ||
                !!this.model.availableTaxableValues?.some((o) => o.annualAssessmentTaxableValueId === selectedId);

            if (isStillAvailable && selectedId !== (taxAuthority.annualAssessmentTaxableValueId ?? null)) {
                this.onTaxableValueChange(taxAuthority, selectedId);
            }
        }
    }

    private roundCurrency(value: number): number {
        return Math.round((value + Number.EPSILON) * 100) / 100;
    }
}
